import type { ReactNode } from "react";

import { cx } from "./cx";

export type Tone = "neutral" | "success" | "warning" | "danger" | "info";

// Figma status pills: a tinted pill with a matching dot.
const BADGE_TONES: Record<Tone, string> = {
  neutral: "bg-[#efecea] text-[#5c5452]",
  success: "bg-[#d9f0e6] text-[#13734d]",
  warning: "bg-[#fce7c6] text-[#7a4700]",
  danger: "bg-[#feddcc] text-[#99380a]",
  info: "bg-sky-500/15 text-sky-700",
};

/** The larger pill of detail screens ("Setup complete", a domain's health): softer tints, 12px text. */
const BADGE_TONES_MD: Record<Tone, string> = {
  neutral: "bg-[#f3ebe7] text-[#5c5551]",
  success: "bg-[#eef8f3] text-[#007955]",
  warning: "bg-[#fef4e5] text-[#853f00]",
  danger: "bg-[#fff1ee] text-[#b52522]",
  info: "bg-[#e8f0fc] text-[#2457a8]",
};

export function Badge({
  tone = "neutral",
  size = "sm",
  children,
}: {
  tone?: Tone;
  /** `sm` is the table-cell pill; `md` the larger one used in headings and detail rows. */
  size?: "sm" | "md";
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center gap-[5px] rounded-full pl-2 font-semibold whitespace-nowrap",
        size === "md" ? "py-1 pr-[9px] text-xs" : "py-[3px] pr-[9px] text-[11.5px]",
        size === "md" ? BADGE_TONES_MD[tone] : BADGE_TONES[tone],
      )}
    >
      <span
        aria-hidden
        className={cx("rounded-full bg-current", size === "md" ? "size-[7px]" : "size-1.5")}
      />
      {children}
    </span>
  );
}
